import { useEffect, useState } from 'react';
import { aplicarCampoEscala, hayEscala, type Base, type CampoEscala, type Escalas, mmDeEscala } from '../../lib/escala';
import { medidas } from '../../lib/formato';

const texto = (n: number) => String(Math.round(n * 100) / 100);

/** Input numérico que no pelea con lo que se está escribiendo: acota al salir del campo. */
function EntradaEscala({ valor, onCambio, sufijo, etiqueta, aria, disabled }: {
  valor: number; onCambio: (n: number) => void; sufijo: string; etiqueta: string; aria: string; disabled?: boolean;
}) {
  const [txt, setTxt] = useState(() => texto(valor));
  const [editando, setEditando] = useState(false);
  useEffect(() => { if (!editando) setTxt(texto(valor)); }, [valor, editando]);
  return (
    <label className="campo">
      <span className="etiqueta">{etiqueta}</span>
      <span className="sufijo">
        <input type="number" inputMode="decimal" step="any" min={0} value={txt} disabled={disabled} aria-label={aria}
          onFocus={() => setEditando(true)}
          onBlur={() => { setEditando(false); setTxt(texto(valor)); }}
          onChange={(ev) => {
            setTxt(ev.target.value);
            const n = Number(ev.target.value);
            if (ev.target.value !== '' && Number.isFinite(n) && n > 0) onCambio(n);
          }} />
        <span>{sufijo}</span>
      </span>
    </label>
  );
}

/**
 * Escala del diseño al estilo de Bambu Studio: porcentaje y milímetros por eje,
 * con «escala uniforme» para conservar la proporción. Límites 1 %–10000 % y 1 mm por lado.
 */
export function ControlesEscala({ nombre, base, escala, onCambio, editable }: {
  nombre: string; base: Base | null; escala: Escalas; onCambio: (e: Escalas) => void; editable: boolean;
}) {
  const [uniforme, setUniforme] = useState(() => escala.escalaX === escala.escalaY);
  const b = base ?? { anchoMm: 0, altoMm: 0 };
  const cambiar = (campo: CampoEscala, valor: number) => onCambio(aplicarCampoEscala(escala, b, campo, valor, uniforme));

  return (
    <div className="escala">
      <div className="escala__campos">
        <EntradaEscala etiqueta="Escala X" sufijo="%" aria={`Escala X de ${nombre}`} disabled={!editable}
          valor={escala.escalaX * 100} onCambio={(n) => cambiar('escalaX', n)} />
        <EntradaEscala etiqueta="Escala Y" sufijo="%" aria={`Escala Y de ${nombre}`} disabled={!editable || uniforme}
          valor={escala.escalaY * 100} onCambio={(n) => cambiar('escalaY', n)} />
        {base && (
          <>
            <EntradaEscala etiqueta="Tamaño X" sufijo="mm" aria={`Tamaño X de ${nombre} en mm`} disabled={!editable}
              valor={mmDeEscala(base.anchoMm, escala.escalaX)} onCambio={(n) => cambiar('anchoMm', n)} />
            <EntradaEscala etiqueta="Tamaño Y" sufijo="mm" aria={`Tamaño Y de ${nombre} en mm`} disabled={!editable || uniforme}
              valor={mmDeEscala(base.altoMm, escala.escalaY)} onCambio={(n) => cambiar('altoMm', n)} />
          </>
        )}
      </div>
      <div className="fila fila--entre escala__pie">
        <label className="check">
          <input type="checkbox" checked={uniforme} disabled={!editable} aria-label={`Escala uniforme de ${nombre}`}
            onChange={(ev) => {
              setUniforme(ev.target.checked);
              if (ev.target.checked && escala.escalaX !== escala.escalaY) onCambio({ escalaX: escala.escalaX, escalaY: escala.escalaX });
            }} />
          Escala uniforme
        </label>
        <span className="fila" style={{ gap: 8 }}>
          <span className="ayuda">
            {base ? `Original ${medidas(base.anchoMm, base.altoMm)} · ` : ''}de 1 % a 10000 %, mínimo 1 mm por lado
          </span>
          <button type="button" className="btn btn--sm" disabled={!editable || !hayEscala(escala)}
            onClick={() => onCambio({ escalaX: 1, escalaY: 1 })}>Volver a 100 %</button>
        </span>
      </div>
    </div>
  );
}
